// Libraries
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {ResponsiveType, useDrawer, useResponsive} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';

// App
import Button from '@shared/design/components/Button';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import FieldValue from '@shared/design/components/Field/FieldValue';
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import EditProjectReferralInformationDrawer from '@shared/modules/Project/components/widgets/components/EditProjectReferralInformationDrawer';
import {ProjectBlockKindType} from '@shared/modules/Project/enums/ProjectBlockKind';
import MobileProjectBlockHeader from 'modules/Project/V2/Show/Blocks/components/MobileProjectBlockHeader';
import ProjectBlockWrapper from 'modules/Project/V2/Show/Blocks/components/ProjectBlockWrapper';

const ResponsiveContainer = Styled.View<{desktop: boolean}>`
  flex-direction: ${({desktop}) => (desktop ? 'row' : 'column')};
`;

const Column = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
`;

const FieldValueWrapper = Styled.View<{desktop: boolean}>`
  ${({desktop}) => desktop && 'flex: 1;'}
`;

const SkeletonField = ({label}: {label: string}) => {
  return (
    <Column style={{flex: 1}}>
      <FieldValue.LabelText>{label}</FieldValue.LabelText>
      <Space height={8} />
      <SkeletonLoader height={SkeletonLoader.HEIGHT.Text} width={180} />
    </Column>
  );
};

const SkeletonBody = ({responsive}: {responsive: ResponsiveType}) => {
  const Container = responsive.desktop ? Row : Column;
  return (
    <Column>
      <Container style={{flex: 1}}>
        <SkeletonField label={'How did you hear about us?'} />
        <Space width={16} />
        <SkeletonField label={'Referral Details'} />
      </Container>
    </Column>
  );
};

const SkeletonComponent = ({responsive}: {responsive: ResponsiveType}) => {
  return (
    <ActionPanel
      BodyComponent={SkeletonBody}
      bodyComponentProps={{responsive}}
      ActionButtonComponent={() => (
        <SkeletonLoader height={SkeletonLoader.HEIGHT.ButtonSmall} width={72} />
      )}
      title={'Referral'}
      style={{width: '100%'}}
    />
  );
};

const ReferralField = ({
  value,
  label,
  isWidget,
}: {
  value: string;
  label: string;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();

  return (
    <FieldValueWrapper desktop={responsive.desktop && !isWidget}>
      <FieldValue value={value} label={label} />
    </FieldValueWrapper>
  );
};

const ReferralInformationFields = ({
  project,
  isWidget,
}: {
  project: ProjectModel;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();

  return (
    <Column>
      <ResponsiveContainer desktop={responsive.desktop && !isWidget}>
        <ReferralField
          label={'How did you hear about us?'}
          value={project.referralSource}
          isWidget={isWidget}
        />
        <Space width={16} height={16} />
        <ReferralField
          label={'Referral Details'}
          value={project.referralDetails}
          isWidget={isWidget}
        />
        {responsive.desktop && (
          <React.Fragment>
            <Space width={16} />
            <Column style={{flex: 1}} />
          </React.Fragment>
        )}
      </ResponsiveContainer>
    </Column>
  );
};

const EditReferralInformationButton = ({
  project,
  refetch,
  isWidget,
}: {
  project: ProjectModel;
  refetch: () => void;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();
  const editReferralInformationDrawer = useDrawer({
    name: 'Edit Project Referral Drawer',
  });

  return (
    <React.Fragment>
      {responsive.desktop && !isWidget ? (
        <Button
          text={'Edit'}
          iconLeft={Icon.Pen}
          onPress={editReferralInformationDrawer.handleOpen}
          isSmall
        />
      ) : (
        <TertiaryButton
          text={`Edit`}
          onPress={editReferralInformationDrawer.handleOpen}
          isResponsive
          isHitSlop
        />
      )}
      <EditProjectReferralInformationDrawer
        key={editReferralInformationDrawer.key}
        project={project}
        isOpen={editReferralInformationDrawer.isOpen}
        handleClose={editReferralInformationDrawer.handleClose}
        refetch={refetch}
      />
    </React.Fragment>
  );
};

const ProjectReferralInformationBlockContent = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  return (
    <ActionPanel
      BodyComponent={ReferralInformationFields}
      bodyComponentProps={{project}}
      ActionButtonComponent={EditReferralInformationButton}
      actionButtonComponentProps={{project, refetch}}
      title={'Referral'}
      style={{width: '100%'}}
    />
  );
};

const ProjectReferralInformationBlockContentMobile = ({
  project,
  refetch,
  isWidget,
}: {
  project: ProjectModel;
  refetch: () => void;
  isWidget?: boolean;
}) => {
  return (
    <React.Fragment>
      <MobileProjectBlockHeader
        title={`Referral`}
        ActionsComponent={EditReferralInformationButton}
        actionsComponentProps={{project, refetch, isWidget}}
      />
      <ReferralInformationFields project={project} isWidget={isWidget} />
    </React.Fragment>
  );
};

const ProjectReferralInformationBlock = ({
  project,
  handleSetPositionY,
  layoutKey,
  index,
  projectBlockKind,
  isWidget,
}: {
  project: ProjectModel;
  handleSetPositionY?: () => void;
  layoutKey?: string;
  index: number;
  projectBlockKind: ProjectBlockKindType;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();

  return (
    <ProjectBlockWrapper
      index={index}
      query={ProjectReferralInformationBlock.query}
      queryVariables={{projectUuid: project.uuid}}
      layoutKey={layoutKey}
      handleSetPositionY={handleSetPositionY}
      SkeletonComponent={() => <SkeletonComponent responsive={responsive} />}
      projectBlockKind={projectBlockKind}
      isWidget={isWidget}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        const {project} = data;
        return (
          <React.Fragment>
            {responsive.desktop && !isWidget ? (
              <ProjectReferralInformationBlockContent project={project} refetch={refetch} />
            ) : (
              <ProjectReferralInformationBlockContentMobile
                project={project}
                refetch={refetch}
                isWidget={isWidget}
              />
            )}
          </React.Fragment>
        );
      }}
    </ProjectBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectReferralInformationBlock.fragment = gql`
  fragment ProjectReferralInformationBlock on Project {
    id
    uuid
  }
`;

ProjectReferralInformationBlock.query = gql`
  ${EditProjectReferralInformationDrawer.fragment}

  query ProjectReferralInformationBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      referralSource
      referralDetails
      organization {
        id
        features {
          isEnabledProjectReferralSourceRequired: isEnabled(
            feature: "PROJECT_REFERRAL_SOURCE_REQUIRED"
          )
        }
      }
      ...EditProjectReferralInformationDrawer
    }
  }
`;

export default ProjectReferralInformationBlock;
